import React, {
  useRef,
} from 'react';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import Tooltip from 'core/components/Tooltip';
import UsesCell from 'views/EMR/Settings/BillingConfigurations/UsesCell';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import pick from 'lodash/pick';
import {
  DeleteConfigCell,
  NPICell,
} from 'views/EMR/Settings/BillingConfigurations/columns';
import {
  BillingProviderInfo,
} from 'views/EMR/Settings/BillingConfigurations/types';
import {
  formatPhone,
} from '@populateui/utils';

const usesKeys = ['billingProviderRules', 'billingProviderPtans', 'claims'];

type TruncatedTooltipCellProps = {
  title?: string | null;
};

function TruncatedTooltipCell({ title }: TruncatedTooltipCellProps) {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <Tooltip title={title} auto ref={ref}>
      <div className="truncate w-full" ref={ref}>
        {title ?? ''}
      </div>
    </Tooltip>
  );
}

const billingProviderColumns: ColumnDef<BillingProviderInfo>[] = [{
  id: 'type',
  accessorKey: 'type',
  columnTitle: 'Type',
  size: 100,
  minSize: 100,
}, {
  id: 'formattedNames',
  accessorKey: 'formattedNames',
  columnTitle: 'Name',
  size: 250,
  minSize: 250,
  cell: ({ row: { original: { formattedNames } } }) => (
    <TruncatedTooltipCell title={getDisplayName(formattedNames)} />
  ),
}, {
  id: 'address',
  accessorKey: 'address',
  columnTitle: 'Address',
  meta: { autoSized: true },
  minSize: 150,
  cell: ({ row: { original: { address } } }) => (
    <div className="flex gap-1 w-full">
      <TruncatedTooltipCell title={address?.formattedAddresses?.oneLine ?? '--'} />
    </div>
  ),
}, {
  id: 'phoneNumber',
  accessorKey: 'phoneNumber',
  columnTitle: 'Phone',
  minSize: 80,
  size: 120,
  cell: ({ row: { original: { phoneNumber } } }) => (
    <TruncatedTooltipCell title={formatPhone(phoneNumber ?? '')} />
  ),
}, {
  id: 'contact',
  accessorKey: 'contact',
  columnTitle: 'Contact',
  minSize: 80,
  size: 120,
  cell: ({ row: { original: { contact } } }) => (
    <TruncatedTooltipCell title={contact} />
  ),
}, {
  id: 'npi',
  accessorKey: 'npi',
  columnTitle: 'NPI',
  size: 350,
  minSize: 300,
  cell: ({ row: { original: { npi } } }) => (<NPICell npi={npi} />),
}, {
  id: 'taxId',
  accessorKey: 'taxId',
  columnTitle: 'Tax ID',
  size: 80,
  minSize: 120,
  cell: ({ row: { original: { taxId } } }) => (
    <div className="flex gap-0.5">
      <span className="flex after:inline-block after:relative after:ml-1 after:content-['•'] last:after:!hidden">
        {taxId?.type}
      </span>
      <span>{taxId?.number}</span>
    </div>
  ),
}, {
  id: 'suspendERAs',
  accessorKey: 'suspendERAs',
  columnTitle: 'Suspend ERAs',
  size: 106,
  minSize: 106,
  cell: ({ row: { original: { suspendERAs } } }) => (
    <div>
      {suspendERAs ? 'Yes' : 'No'}
    </div>
  ),
}, {
  id: 'stats',
  accessorKey: 'stats',
  columnTitle: 'Uses',
  size: 96,
  minSize: 48,
  cell: ({ row: { original: { stats, billingProviderId } } }) => (
    <UsesCell
      {...pick(stats, usesKeys) as Record<string, number>}
      id={billingProviderId ?? 0}
    />
  ),
}, {
  id: 'actions',
  accessorKey: 'actions',
  columnTitle: '',
  size: 48,
  cell: ({ row: { original: { billingProviderId, stats } } }) => (
    <DeleteConfigCell
      type="billingProvider"
      configTitle="Billing Provider"
      payload={{ billingProviderId }}
      canDelete={stats?.canDelete ?? false}
      tooltip={!stats?.canDelete ? 'Cannot delete a provider that is in use' : undefined}
    />
  ),
}];

export default billingProviderColumns;
